import { Formik } from "formik";
import { useRef, useState } from "react";
import CMSFormTelephone from "../../formFields/CMSFormTelephone";
import CMSFormInput from "../../formFields/CMSFormInput";
import GeneralButton from "../../common/GeneralButton";
import ContainerLoading from "../../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import success from "../../../assets/images/success.png";
import { useToast } from "../../contexts/ToastContext";
import { personalInfoSchema } from "../../utils/validations";
import { useNavigate } from "react-router-dom";
import CMSFormSelect from "../../formFields/CMSFormSelect";
import { FiArrowLeft } from "react-icons/fi";
import CMSFormImage from "../../formFields/CMSFormImage";
import { formatToLabelValue, formatToSameLabelValue } from "../../utils/commonHelper";
import { useGeneral } from "../../contexts/GeneralContext";
import axios from "../../../config/axios";
const EditPersonalInfo = ({ content }) => {
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success" | "error"
  const { addToast } = useToast();
  const { extraContent } = useGeneral();
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    confirmEmail: useRef(null),
    country: useRef(null),
    phone: useRef(null),
    gender: useRef(null),
    image: useRef(null),
    age: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;

    // Smoothly scroll the nearest scrollable parent
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // Give the scroll some time to finish, then focus
    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500); // adjust based on your animation speed
  };

  return (
    <>
      <div className="mt-6 flex flex-col gap-6 bg-[#DEE2DD]/40 rounded-[15px] p-6 lg:px-12 w-full relative">
        <div className="flex items-center gap-4 mb-0">
          {/* <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 pe-3 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
          >
            <FiArrowLeft size={30} />
          </button> */}
          <p className="text-24pt">Edit Personal Info</p>
        </div>
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
            <ContainerLoading />
          </div>
        )}

        {status !== "success" && (
          <Formik
            initialValues={{
              firstName: content?.firstName || "",
              lastName: content?.lastName || "",
              nationality: content?.nationality || "",
              mobile: content?.mobile || "",
              image: content?.image || "",
            }}
            validationSchema={personalInfoSchema}
            onSubmit={async (values, { resetForm }) => {
              // console.log("Submitting values:", values);
              setStatus("loading");

              try {
                // ✅ Prepare FormData
                const formData = new FormData();
                Object.keys(values).forEach((key) => {
                  if (values[key] !== undefined && values[key] !== null) {
                    formData.append(key, values[key]);
                  }
                });

                // ✅ Send API request as multipart/form-data
                const res = await axios.post(
                  "/dashboard/profile/personal-info",
                  formData,
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                  }
                );
                // ✅ Handle response
                if (res.status === 200 || res.status === 201) {
                  addToast("Profile details updated successfully", "success");

                  // ✅ Get existing user object
                  const storedUser =
                    JSON.parse(localStorage.getItem("loggedInUser")) || {};

                  // ✅ Merge updated user data (save only image name)
                  const updatedUser = {
                    ...storedUser,
                    ...res.data?.item,
                  };

                  // ✅ Save to localStorage
                  localStorage.setItem(
                    "loggedInUser",
                    JSON.stringify(updatedUser)
                  );

                  resetForm({ values });
                  setStatus("idle");
                  navigate("/account/profile");
                } else {
                  throw new Error("Update failed");
                }
              } catch (err) {
                console.error("Profile update failed:", err);
                setError("Failed to update details. Please try again.");
                setTimeout(() => {
                  setError("");
                }, 5000);
                setStatus("idle");
              }
            }}
          >
            {(form) => (
              <div className="w-full grid grid-cols-1 gap-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <CMSFormInput
                    name="firstName"
                    label="First Name"
                    className="1"
                    form={form}
                    ref={refs.firstName}
                    required
                  />
                  <CMSFormInput
                    name="lastName"
                    label="Last Name"
                    className="1"
                    form={form}
                    ref={refs.lastName}
                    required
                  />
                </div>

                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <CMSFormInput
                    name="email"
                    label="Email"
                    form={form}
                    ref={refs.email}
                    className="1"
                    required
                  />
                  <CMSFormInput
                    name="confirmEmail"
                    label="Confirm Email Address"
                    form={form}
                    ref={refs.confirmEmail}
                    className="1"
                    required
                  />
                </div> */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <CMSFormTelephone
                    name="mobile"
                    label="Phone"
                    form={form}
                    ref={refs.phone}
                    required
                  />
                  <CMSFormSelect
                    name="nationality"
                    label="Nationality"
                    selected={form.values.nationality}
                    className="1"
                    placeholder={{ label: "Nationality", value: "" }}
                    form={form}
                    ref={refs.country}
                    onChange={form.handleChange}
                    required
                    options={formatToSameLabelValue(extraContent?.nationalities)}
                  />

                  <CMSFormImage
                    name="image"
                    label="Profile Image"
                    form={form}
                    ref={refs.image}
                    required={true}
                    helpMessage={{ message: "Upload the logo in png format." }}
                    moduleName="clients"
                  />
                </div>
                {error && (
                  <div
                    className={`relative transition-all duration-300 ease-in-out ${
                      error
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 -translate-y-2"
                    }`}
                  >
                    {error && <span className="errorMessage">{error}</span>}
                  </div>
                )}
                <div className="flex items-end justify-end">
                  <GeneralButton
                    text="Submit"
                    type="submit"
                    className="text-21pt px-4"
                    textColor="#fff"
                    onClick={() => {
                      form.handleSubmit();
                      setTimeout(() => scrollToFirstError(form?.errors), 100);
                    }}
                  />
                </div>
              </div>
            )}
          </Formik>
        )}
      </div>
    </>
  );
};

export default EditPersonalInfo;
